/**
 * Cross-subdomain conversion attribution for orders created on app.klikkie.com.
 *
 * utm/click IDs are captured by the marketing site + funnel and persisted in a
 * `.klikkie.com` cookie (`_kk_utm`) — see klikkie-website `ecommerceTracking.js`
 * and klikkie-agent `utils/attribution.ts`. sessionStorage is per-origin, so the
 * member app can't see the original landing's utm; the cookie crosses the hop
 * (like `_fbp`/`_ga`/`_fbc`). This reads that cookie so member-app orders inherit
 * the original ad attribution the Adyen webhook fires server-side (KL-14380).
 */

const ATTRIBUTION_COOKIE = '_kk_utm';

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'awc',
  'fbclid',
  'gclid',
  'wbraid',
  'gbraid',
  'msclkid',
  'ttclid',
  'epik',
  'scid',
  'campaign_id',
  'ad_id',
  'adset_id',
  'placement',
  'site_source_name',
  'fbpid',
  'clid',
  'sid',
];

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

/** Read the cross-subdomain `_kk_utm` cookie into a bag ({} when absent/corrupt). */
const getAttributionCookie = () => {
  try {
    const raw = getCookie(ATTRIBUTION_COOKIE);
    if (!raw) return {};
    const parsed = JSON.parse(decodeURIComponent(raw));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

/** utm/click IDs present on the current app URL (rare here, but freshest when set). */
const captureFromUrl = () => {
  const bag = {};
  if (typeof window === 'undefined') return bag;
  const params = new URLSearchParams(window.location.search);
  UTM_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) bag[key] = value;
  });
  return bag;
};

// Cookie first (cross-subdomain survivor), current URL overlaid on top (freshest for this origin).
const getBag = () => ({ ...getAttributionCookie(), ...captureFromUrl() });

/**
 * The `IUserAgent.utm` subset (utm_* + awc). Returns undefined when no utm was
 * captured, so callers can omit the field rather than send empty strings.
 */
export const getCapturedUtm = () => {
  const bag = getBag();
  const utm_source = bag.utm_source || '';
  const utm_medium = bag.utm_medium || '';
  const utm_campaign = bag.utm_campaign || '';
  const utm_term = bag.utm_term || '';
  const utm_content = bag.utm_content || '';
  const awc = bag.awc;

  if (!utm_source && !utm_medium && !utm_campaign && !utm_term && !utm_content && !awc) {
    return undefined;
  }
  return {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_term,
    utm_content,
    ...(awc ? { awc } : {}),
  };
};

/**
 * The full `IUserAgent.attribution` bag: utm + click IDs + `_fbp`/`_fbc` cookies
 * + GA4 client_id + landing context. Empty values are dropped to keep the order
 * doc compact.
 */
export const buildAttributionPayload = () => {
  const bag = getBag();
  const fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');
  // GA4 client_id from `_ga` cookie: `GA1.1.<client_id>.<timestamp>`.
  const gaCookie = getCookie('_ga') || '';
  const gaClientId = gaCookie ? gaCookie.split('.').slice(-2).join('.') : '';

  const candidate = {
    ...bag,
    fbc: fbc || null,
    fbp: fbp || null,
    ga_client_id: gaClientId || null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    landing_url: typeof window !== 'undefined' ? window.location.href : null,
  };

  return Object.fromEntries(
    Object.entries(candidate).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
};
